import { useEffect, useState, useMemo, FC } from "react"
import { Card, Spinner } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js"
import { Bar } from "react-chartjs-2"
import type { ChartOptions } from "chart.js"
import API from "@/common/API"
import { parsedUser } from "@/common/GetCurrentUser"
import { FormatMoney } from "@/common/helpers"

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
)

interface SalesData {
    year: number
    month: number
    amount: number
}

interface ProcessedData {
    labels: string[]
    datasets: {
        label: string
        data: number[]
        backgroundColor: string
        borderColor: string
        borderWidth: number
        borderRadius: number
    }[]
}

interface Props {
    placeId: string
}

const SalesByMonthChart: FC<Props> = ({ placeId }) => {
    const { t } = useTranslation("global")
    const user = parsedUser()
    const [loading, setLoading] = useState<boolean>(true)
    const [salesData, setSalesData] = useState<SalesData[]>([])
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        const fetchSalesData = async () => {
            if (!user?.OrganizationId || !placeId) {
                setLoading(false)
                return
            }

            try {
                setLoading(true)
                const response = await API.getAction(
                    `dashboard/SalesByMonthGeneralByPlace?OrganizationId=${user.OrganizationId}&placeId=${placeId}`
                )

                if (response?.data?.response && Array.isArray(response.data.response)) {
                    setSalesData(response.data.response)
                } else {
                    setSalesData([])
                }
                setError(null)
            } catch (err) {
                console.error("Error fetching place sales data:", err)
                setError((err as Error)?.message || "Failed to load sales data")
                setSalesData([])
            } finally {
                setLoading(false)
            }
        }

        fetchSalesData()
    }, [user?.OrganizationId, placeId])

    // Process data for chart
    const chartData: ProcessedData | null = useMemo(() => {
        if (!salesData || salesData.length === 0) return null

        // Filter out invalid data
        const validData = salesData.filter(
            (d) => d.year != null && d.month != null && d.month >= 1 && d.month <= 12
        )

        if (validData.length === 0) return null

        // Get unique years and sort them
        const years = Array.from(new Set(validData.map((d) => d.year))).sort()

        // Month labels
        const monthNames = [
            "Jan", "Feb", "Mar", "Apr", "May", "Jun",
            "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
        ]

        // Create a map for each year
        const yearDataMap = new Map<number, Map<number, number>>()

        years.forEach((year) => {
            yearDataMap.set(year, new Map())
        })

        // Fill the map with sales data
        validData.forEach((item) => {
            const yearMap = yearDataMap.get(item.year)
            if (yearMap) {
                yearMap.set(item.month, item.amount || 0)
            }
        })

        // Find all months that have data across all years
        const allMonths = Array.from(
            new Set(validData.map((d) => d.month))
        ).sort((a, b) => a - b)

        // Colors for each year
        const colors = [
            { bg: "rgba(59, 130, 246, 0.7)", border: "rgba(59, 130, 246, 1)" },
            { bg: "rgba(16, 185, 129, 0.7)", border: "rgba(16, 185, 129, 1)" },
            { bg: "rgba(245, 158, 11, 0.7)", border: "rgba(245, 158, 11, 1)" },
            { bg: "rgba(239, 68, 68, 0.7)", border: "rgba(239, 68, 68, 1)" },
            { bg: "rgba(168, 85, 247, 0.7)", border: "rgba(168, 85, 247, 1)" },
        ]

        const datasets = years
            .map((year, index) => {
                const yearMap = yearDataMap.get(year)
                if (!yearMap || year == null) return null
                const color = colors[index % colors.length]

                return {
                    label: String(year),
                    data: allMonths.map((month) => yearMap.get(month) || 0),
                    backgroundColor: color.bg,
                    borderColor: color.border,
                    borderWidth: 2,
                    borderRadius: 6,
                }
            })
            .filter((dataset): dataset is NonNullable<typeof dataset> => dataset !== null)

        return {
            labels: allMonths.map((m) => (m >= 1 && m <= 12 ? monthNames[m - 1] : "")),
            datasets,
        }
    }, [salesData])

    const chartOptions: ChartOptions<"bar"> = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: "top" as const,
                labels: {
                    font: {
                        size: 12,
                    },
                    padding: 15,
                },
            },
            title: {
                display: true,
                text: t("Sales By Month") + " - " + t("Year Comparison"),
                font: {
                    size: 16,
                    weight: "bold" as const,
                },
                padding: {
                    bottom: 20,
                },
            },
            tooltip: {
                callbacks: {
                    label: function (context: { dataset: { label?: string }, parsed: { y: number | null } }) {
                        let label = context.dataset.label || ""
                        if (label) {
                            label += ": "
                        }
                        if (context.parsed.y !== null) {
                            label += FormatMoney(context.parsed.y, 2)
                        }
                        return label
                    },
                },
            },
        },
        scales: {
            x: {
                grid: {
                    display: false,
                },
                ticks: {
                    font: {
                        size: 11,
                    },
                },
            },
            y: {
                beginAtZero: true,
                grid: {
                    color: "rgba(0, 0, 0, 0.05)",
                },
                ticks: {
                    font: {
                        size: 11,
                    },
                    callback: function (value: string | number) {
                        return FormatMoney(Number(value), 0)
                    },
                },
            },
        },
    }

    if (loading) {
        return (
            <Card className="shadow-sm">
                <Card.Body>
                    <div className="text-center py-4">
                        <Spinner animation="border" role="status" size="sm">
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                        <div className="mt-2 small">{t("Loading sales data")}...</div>
                    </div>
                </Card.Body>
            </Card>
        )
    }

    if (error) {
        return (
            <Card className="shadow-sm">
                <Card.Body>
                    <div className="alert alert-warning mb-0" role="alert">
                        {t("Error")}: {error}
                    </div>
                </Card.Body>
            </Card>
        )
    }

    if (!chartData) {
        return null // Don't show anything if no data
    }

    return (
        <Card className="shadow-sm">
            <Card.Body>
                <div style={{ height: "350px" }}>
                    <Bar data={chartData} options={chartOptions} />
                </div>
            </Card.Body>
        </Card>
    )
}

export default SalesByMonthChart
